<template>
  <ContentError />
</template>

<script lang="tsx" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CIcon from '@lib/components/CIcon/CIcon.vue'

interface Props {
  expired?: boolean
  restricted?: boolean
  onlyIcon?: boolean
  onlyTag?: boolean
  showHover?: boolean
}

const props = defineProps<Props>()
const { t } = useI18n()

const expiredConfig = {
  iconName: 'regular-clock-circle-exclamation',
  iconType: 'fa-kit' as const,
  text: t('canvasUI.CContentError.expired.text'),
  hoverText: t('canvasUI.CContentError.expired.hoverText'),
  borderColor: '#BC1637',
  textColor: '#BC1637',
  backgroundColor: '#FEF7F5',
}

const restrictedConfig = {
  iconName: 'user-lock',
  iconType: 'far' as const,
  text: t('canvasUI.CContentError.restricted.text'),
  hoverText: t('canvasUI.CContentError.restricted.hoverText'),
  borderColor: '#E5C302',
  textColor: '#C4A401',
  backgroundColor: '#FFFDEA',
}

const getConfig = () => {
  if (props.expired) {
    return expiredConfig
  } else if (props.restricted) {
    return restrictedConfig
  }

  return {
    iconName: '',
    iconType: 'fa' as const,
    text: '',
    hoverText: '',
    borderColor: '',
    textColor: '',
    backgroundColor: '',
  }
}

const text = computed(() => getConfig().text)
const hoverText = computed(() => (props.onlyIcon ? getConfig().text : getConfig().hoverText))
const backgroundColor = computed(() => getConfig().backgroundColor)
const iconType = computed(() => getConfig().iconType)
const borderColor = computed(() => getConfig().borderColor)
const iconName = computed(() => getConfig().iconName)
const textColor = computed(() => getConfig().textColor)

const shouldShowWrapper = computed(() => {
  return !props.onlyIcon && !props.onlyTag
})

const renderContext = () => {
  const classes = props.onlyIcon ? 'w-6 h-6 justify-center' : 'px-2'

  return (
    <div
      v-tooltip={props.showHover || props.onlyIcon ? hoverText.value : undefined}
      class={['flex items-center gap-1 rounded-full font-600 font-size-3 tracking-[.4px]', classes].join(' ')}
      style={{ background: backgroundColor.value, color: textColor.value }}
    >
      <CIcon faType={iconType.value} fixedWidth icon={iconName.value} size="12px" />
      {!props.onlyIcon && <span class="font-medium">{text.value}</span>}
    </div>
  )
}

const ContentError = () => {
  if (!shouldShowWrapper.value) {
    return renderContext()
  }

  return (
    <div
      class="absolute inset-0 border-solid border-2 flex items-start justify-center pa-4"
      style={{ borderColor: borderColor.value }}
    >
      {renderContext()}
    </div>
  )
}
</script>
